import { createSlice } from '@reduxjs/toolkit';
import { BILLING_TYPE, ORDER_STATUS, SERVICE_TYPES } from '../../utils/constants';
import { calculatePlatformFees, calculateTax } from '../../utils/helpers';
import { ORDER_PLACEMENT } from '../types';

const orderPlacementSlice = createSlice({
    name: ORDER_PLACEMENT,
    reducers: {
        setOrderDetails(state, action) {
            state.orderDetails = action.payload;
            state.filters = {
                search: '',
                veg: false,
                offer: false,
                rating: '',
                sortBy: ''
            };
            state.menuFilters = {
                search: '',
                veg: false,
                offer: false,
                rating: '',
                sortBy: ''
            };
        },
        placeOrderRequest() { },
        payOrderRequest() { },
        setOrderPaymentData(state, action) {
            state.orderPaymentData = action.payload;
        },
        paymentSuccessRequest() { },
        paymentFailedRequest() { },
        setFeedback(state, action) {
            state.feedback = action.payload;
        },
        sendFeedbackRequest() { },
        getMenuDetailsRequest() { },
        getMenuDetailsSuccess(state, action) {
            const { data, hotels, foodCourtName, foodCourtId, sliderImages, foodCourtBannerData } = action.payload;
            if (foodCourtId) {
                // this is wrriten to reset the Food Court Name every time
                let fcName = '';
                let fcId = null;
                fcName = foodCourtName;
                fcId = foodCourtId;

                // Handling food court scenario
                state.hotelListData = hotels;
                state.rawHotelListData = hotels;
                state.rawFoodCourtMenuData = data;
                state.hotelBillingType = { show: false, type: BILLING_TYPE[0].value };
                state.foodCourtName = fcName;
                state.foodCourtId = fcId;
                state.hotelSliderImages = sliderImages;
                state.foodCourtBannerData = foodCourtBannerData;

                state.flatFcHotels = Object.values(data).flatMap(hotel => ({
                    id: hotel.hotelId,
                    name: hotel.hotelName
                }));
                state.flatFcMenus = Object.values(data).flatMap(hotel =>
                    hotel.data.flatMap(category =>
                        category.name?.toLowerCase() === 'offers'
                            ? []
                            : category.menus
                    )
                );
            } else {
                // handling single hotel scenario
                const menuData = data[hotels[0]?.id]?.data || [];
                state.currentHotelData = hotels[0] || {};
                state.rawMenuData = menuData;
                state.flatMenuData = menuData.flatMap(category =>
                    category.menus.map(item => ({
                        ...item,
                        categoryName: category.name,
                        searchableText: `${item.name} ${category.name}`
                    }))
                );
                state.menuCardData = menuData;
                state.selectedCategory = menuData?.[0]?.id;
                state.menuItems = menuData?.[0]?.menus || [];
                state.managers = data[hotels[0]?.id]?.managers || [];
                state.tables = data[hotels[0]?.id]?.tables || [];
                state.hotelBillingType = { show: false, type: hotels[0]?.billingType };
            }
        },
        setMenuCardSelectedCategory(state, action) {
            state.selectedCategory = action.payload;
        },
        setMenuItems(state, action) {
            state.menuItems = action.payload;
        },
        setActiveSubCategoryItem(state, action) {
            state.activeSubCategoryItemId = action.payload;
        },
        setCart(state, action) {
            state.cart = action.payload;
        },
        resetMenuFilters(state, action) {
            state.showMenu = true;
            state.menuCardData = state.rawMenuData;
            state.selectedCategory = state.rawMenuData?.[0]?.id;
            state.menuItems = state.rawMenuData?.[0]?.menus || [];
            state.showOrder = false;
            if (action.payload?.filters) {
                state.menuFilters = {
                    search: '',
                    veg: false,
                    offer: false,
                    rating: '',
                    sortBy: ''
                };
            }
        },
        setShowOrder(state, action) {
            state.showOrder = action.payload;
        },
        setSearchedData(state, action) {
            state.menuCardData = action.payload;
        },
        // setTokenOption(state, action) {
        //     state.showTokenOption = action.payload;
        // },
        setFoodCourtHotelData(state, action) {
            if (!action.payload) {
                state.menuFilters = {
                    search: '',
                    veg: false,
                    offer: false,
                    rating: '',
                    sortBy: ''
                };
                state.currentHotelData = {};
            } else {
                const hotelData = state.rawFoodCourtMenuData[action.payload] || {};
                const menuData = hotelData.data;
                state.currentHotelData = (state.rawHotelListData || [])?.find(obj => obj.id === action.payload) || {};
                state.selectedCategory = menuData?.[0]?.id;
                state.menuCardData = menuData;
                state.rawMenuData = menuData;
                state.menuItems = menuData?.[0]?.menus || [];
                state.managers = hotelData[action.payload]?.managers || [];
                state.tables = hotelData[action.payload]?.tables || [];
                state.flatMenuData = menuData.flatMap(category =>
                    category.menus.map(item => ({
                        ...item,
                        categoryName: category.name,
                        name: `${item.name}`,
                        description: `${item.description}`
                    }))
                );
            }
        },
        setHotelBillingType(state, action) {
            state.hotelBillingType = action.payload;
        },
        setHotelListData(state, action) {
            state.hotelListData = action.payload;
        },
        setCustomerOrder(state, action) {
            state.customerOrder = action.payload;
        },
        setTokenCode(state, action) {
            state.tokenCode = action.payload;
        },
        setOrderTotals(state, action) {
            state.orderTotals = action.payload;
        },
        notifyManagersRequest() { },
        setServiceType(state, action) {
            state.serviceType = action.payload;
        },
        setDeliveryAddress(state, action) {
            state.deliveryAddress = action.payload;
        },
        getCurrentOrderRequest() { },
        setCurrentOrder(state, action) {
            const { data, hotels, customerId, tokenId, deliveryAddress = '' } = action.payload;
            const cart = {};
            const completeOrder = {};
            const bill = { subTotal: 0 };
            const orderTotals = { completed: 0, grandTotal: 0 };

            data
                .forEach((order) => {
                    const obj = {
                        id: order.id,
                        name: order.name,
                        thumbnailLink: order.thumbnailLink,
                        hotelId: order.hotelId,
                        hotelName: order.hotelName,
                        status: order.status,
                        price: order.price,
                        count: order.count,
                        variant: order.variant
                    };
                    obj.subCategory = Object.keys(order.subCategory || {}).map((item) => {
                        obj[`sub#${item.replace(/\s/g, '-')}`] = order.subCategory[item];
                        return item;
                    });
                    if (order.status === ORDER_STATUS.pending) {
                        cart[order.id] = obj;
                    }

                    if (!Object.hasOwn(completeOrder, order.hotelId)) {
                        completeOrder[order.hotelId] = [];
                    }
                    completeOrder[order.hotelId].push(obj);
                    bill.subTotal += (Number(order.price) * Number(order.count));

                    let paymentStatus = [];
                    if (hotels.length > 1) {
                        paymentStatus = [ORDER_STATUS.prepared, ORDER_STATUS.served];
                    } else if (hotels[0].billingType === BILLING_TYPE[0].value) {
                        paymentStatus = [ORDER_STATUS.prepared, ORDER_STATUS.served];
                    } else {
                        paymentStatus = [ORDER_STATUS.served];
                    }

                    if (paymentStatus.includes(order.status)) {
                        orderTotals.completed += (Number(order.price) * Number(order.count));
                    }
                    orderTotals.grandTotal += (Number(order.price) * Number(order.count));
                });

            state.orderDetails.customerId = customerId;
            state.customerTokenId = tokenId;
            state.cart = cart;
            state.orderTotals = orderTotals;

            if (hotels.length > 1) {
                bill.platformCharges = calculatePlatformFees(bill.subTotal);
                bill.totalPrice = bill.subTotal + bill.platformCharges;
            } else {
                let tax = 0;
                if (hotels[0].tax) {
                    tax = Number(calculateTax(bill.subTotal));
                }
                bill.sgst = tax;
                bill.cgst = tax;
                bill.platformCharges = 0;
                if (hotels[0].paymentGateway) {
                    bill.platformCharges = calculatePlatformFees(bill.subTotal);
                }
                bill.totalPrice = bill.sgst + bill.cgst + bill.subTotal + bill.platformCharges;
            }
            state.customerOrder = {
                show: false,
                bill,
                data: completeOrder
            };

            state.deliveryAddress = deliveryAddress;
        },
        setClassicMenuData(state, action) {
            const { menu, pages, hotel } = action.payload;
            state.currentHotelData = hotel;

            state.classicMenuData.menu = menu;
            state.classicMenuData.pages = pages;
            state.hotelBillingType = { show: false, type: BILLING_TYPE[0].value };

            state.classicMenuData.show = true;
        },
        setCurrent(state, action) {
            state.classicMenuData.current = action.payload;
        },
        setShowPagePicker(state, action) {
            state.classicMenuData.pagePicker = action.payload;
        },
        setShowClassicCart(state, action) {
            state.classicMenuData.showClassicCart = action.payload;
        },
        resetStateRequest(state) {
            state.feedback = { show: false, data: {} };
            state.cart = {};
            state.orderPaymentData = false;
            state.customerOrder = { show: false, data: {}, bill: {} };
            state.tokenCode = { show: false, copied: false };
            state.orderTotals = { completed: 0, grandTotal: 0 };
            state.orderDetails = { tableId: state.orderDetails.tableId };
            state.customerTokenId = false;
            state.filters = {
                search: '',
                veg: false,
                offer: false,
                rating: '',
                sortBy: ''
            };
            state.menuFilters = {
                search: '',
                veg: false,
                offer: false,
                rating: '',
                sortBy: ''
            };
            state.deliveryAddress = '';
            if (state.foodCourtId) {
                state.currentHotelData = {};
            }
        },
        setMenuFilters(state, action) {
            if (!Object.keys(state.currentHotelData).length) {
                state.filters = { ...state.filters, ...action.payload };
            } else {
                state.menuFilters = { ...state.menuFilters, ...action.payload };
            }
        },
        setFilteredData(state, action) {
            const { hotels, menuItems } = action.payload;
            state.filteredData.hotels = hotels.data.slice(0, 2) || [];
            state.filteredData.allOutlets = hotels.data || [];
            state.filteredData.hasMoreHotels = hotels.total > 2;
            state.filteredData.menuItems = menuItems.data || [];
        },
        setShowAllSearchedHotels(state, action) {
            state.filteredData.hotels = state.filteredData.allOutlets;
            state.filteredData.hasMoreHotels = false;
        },
        setIsFiltering(state, action) {
            state.isFilterring = action.payload;
        },
        setUserRatings(state, action) {
            state.userRating = action.payload;
        },
        setOrderSuccessAnimation(state, action) {
            state.orderSuccessAnimation = action.payload;
        }
    },
    initialState: {
        feedback: { show: false, data: {} },
        hotelBillingType: false,
        hotelListData: [],
        rawHotelListData: [],
        currentHotelData: {},
        rawFoodCourtMenuData: {},
        rawMenuData: [],
        menuCardData: [],
        selectedCategory: '',
        menuItems: [],
        activeSubCategoryItemId: '',
        cart: {},
        showOrder: false,
        orderDetails: {},
        showMenu: false,
        orderPaymentData: false,
        // showTokenOption: false,
        customerOrder: { show: false, data: {}, bill: {} },
        tokenCode: { show: false, copied: false },
        managers: [],
        customerTokenId: false,
        orderTotals: { completed: 0, grandTotal: 0 },
        foodCourtName: '',
        foodCourtId: '',
        foodCourtBannerData: { thumbnailVideo: '', icon: '' },
        serviceType: SERVICE_TYPES.DINE_IN,
        deliveryAddress: '',
        classicMenuData: {
            hotelName: '',
            pages: [],
            menu: {},
            show: false,
            current: 0,
            pagePicker: false,
            showClassicCart: false
        },
        filters: {
            search: '',
            veg: false,
            offer: false,
            rating: '',
            sortBy: ''
        },
        menuFilters: {
            search: '',
            veg: false,
            offer: false,
            rating: '',
            sortBy: ''
        },

        // this is filtered data from search api
        filteredData: {
            hotels: [],
            menuItems: [],
            hotelsCount: 0
        },
        isFilterring: false,
        hotelSliderImages: {},

        flatMenuData: [],
        flatFcMenus: [],
        flatFcHotels: [],

        userRating: {
            hotels: {},
            menus: {}
        },
        orderSuccessAnimation: false
    }
});
export const {
    getMenuDetailsRequest,
    getMenuDetailsSuccess,
    placeOrderRequest,
    paymentSuccessRequest,
    paymentFailedRequest,
    setFeedback,
    sendFeedbackRequest,
    setMenuCardSelectedCategory,
    setMenuItems,
    setActiveSubCategoryItem,
    setCart,
    resetMenuFilters,
    setShowOrder,
    setSearchTerm,
    setOrderDetails,
    setSearchedData,
    payOrderRequest,
    setOrderPaymentData,
    setFoodCourtHotelData,
    setHotelBillingType,
    setHotelListData,
    setCustomerOrder,
    setTokenCode,
    setOrderTotals,
    notifyManagersRequest,
    // setTokenOption,
    setServiceType,
    setDeliveryAddress,
    getCurrentOrderRequest,
    setCurrentOrder,
    resetStateRequest,

    // classic menu states
    setClassicMenuData,
    setCurrent,
    setShowPagePicker,
    setShowClassicCart,

    // filters state
    setMenuFilters,
    setFilteredData,
    setIsFiltering,
    setShowAllSearchedHotels,

    setUserRatings,
    setOrderSuccessAnimation
} = orderPlacementSlice.actions;

export const orderPlacementReducer = orderPlacementSlice.reducer;
